import deepEqual from "fast-deep-equal";
import { useCallback, use, useState } from "react";
import { createApiResponse } from "@entrilia/shared/api/axiosHelper";
import useFetch from "@entrilia/shared/hooks/useFetch";
import adminApi from "../../api/adminApi";
import type {
  BusinessCentralObjectType,
  Field,
  FieldIdToLookupReferences,
  FieldIdToValueMap,
  FieldsCitationsMap,
  FieldsConfidenceMap,
  LookupObjectReference,
  ObjectTypeToLookupReferences,
  PageLayout,
  RelatedObjectsUpdateRequests,
} from "../../api/types/objectTypes";
import usePageLeaveConfirmation from "../../hooks/usePageLeaveConfirmation";
import type { EmailFieldEditMode, FieldValuesState } from "./FieldValuesContext";
import { isAnyRequiredFieldEmpty } from "./fieldValuesManagement";
import ObjectCardContext from "./ObjectCardContext";

export interface FieldValuesSectionProps {
  objectType: string;
  objectId: string;
  businessCentralType: BusinessCentralObjectType | undefined;
  fields: Field[];
  cardLayout: PageLayout;
  fieldValues: FieldIdToValueMap;
  fieldsConfidence?: FieldsConfidenceMap;
  fieldsCitations?: FieldsCitationsMap;
  lookupObjects?: FieldIdToLookupReferences;
  allowEditing: boolean;
  saveFieldValues: (fieldValues: FieldIdToValueMap, relatedObjects?: RelatedObjectsUpdateRequests) => Promise<boolean>;
  emailFieldEditMode?: EmailFieldEditMode;
  isLoading?: boolean;
}

export interface FieldValuesEditor {
  fieldValuesState: FieldValuesState;
  lookupOptionsByObjectType: ObjectTypeToLookupReferences | undefined;
  hasUnsavedFieldEdits: boolean;
  areButtonsDisabled: boolean;
  isSaveDisabled: boolean;
  isBusy: boolean;
  handleFieldValuesStateChange: (stateUpdate: Partial<FieldValuesState>) => void;
  applyFieldValues: (fieldValues: FieldIdToValueMap) => void;
  handleAddLookupOption: (objectType: string, lookupObjectReference: LookupObjectReference) => void;
  handleSaveClick: () => Promise<void>;
  handleCancelClick: () => void;
}

const useFieldValuesEditor = (
  { objectType, objectId, fields, fieldValues, allowEditing, saveFieldValues, isLoading }: FieldValuesSectionProps,
  hasUnsavedEditsOutsideFields = false
): FieldValuesEditor => {
  const objectCard = use(ObjectCardContext);

  const [isSaving, setIsSaving] = useState(false);

  const [fieldValuesState, setFieldValuesState] = useState<FieldValuesState>({
    fieldValues,
    isValid: true,
    fieldEditState: "viewing",
  });

  const setHasUnsavedEdits = objectCard?.setHasUnsavedEdits;

  const { setPromptOnPageLeave } = usePageLeaveConfirmation(false);

  const getLookupOptions = useCallback(
    () =>
      allowEditing && fields.some((f) => f.type === "Lookup") && !isLoading
        ? adminApi.getLookupOptions(objectType, objectId)
        : Promise.resolve(createApiResponse<ObjectTypeToLookupReferences>({})),
    [allowEditing, fields, objectType, objectId, isLoading]
  );

  const [lookupOptionsByObjectType, , { isFetching: isFetchingLookupOptions, setData: setLookupOptions }] =
    useFetch(getLookupOptions);

  const handleAddLookupOption = useCallback(
    (objectType: string, lookupObjectReference: LookupObjectReference) => {
      setLookupOptions((prev) => {
        if (!prev) {
          return { [objectType]: [lookupObjectReference] };
        }

        const updatedOptions: ObjectTypeToLookupReferences = { ...prev };
        if (!updatedOptions[objectType]) {
          updatedOptions[objectType] = [];
        }

        if (updatedOptions[objectType].some((option) => option.objectId === lookupObjectReference.objectId)) {
          return prev;
        }

        updatedOptions[objectType].push(lookupObjectReference);

        return updatedOptions;
      });
    },
    [setLookupOptions]
  );

  const hasUnsavedChanges = (editedFieldValues: FieldIdToValueMap) =>
    !deepEqual(editedFieldValues, fieldValues) || hasUnsavedEditsOutsideFields;

  const applyFieldValues = (newFieldValues: FieldIdToValueMap) => {
    setPromptOnPageLeave(true);
    setFieldValuesState((prev) => ({ ...prev, fieldValues: { ...prev.fieldValues, ...newFieldValues } }));
    setHasUnsavedEdits?.(hasUnsavedChanges({ ...fieldValuesState.fieldValues, ...newFieldValues }));
  };

  const handleFieldValuesStateChange = (stateUpdate: Partial<FieldValuesState>) => {
    setPromptOnPageLeave(true);
    setFieldValuesState((prev) => ({ ...prev, ...stateUpdate }));
    setHasUnsavedEdits?.(hasUnsavedChanges(stateUpdate.fieldValues ?? fieldValuesState.fieldValues));
  };

  const handleSaveClick = async () => {
    const changedFieldValues = Object.entries(fieldValuesState.fieldValues).reduce<FieldIdToValueMap>(
      (result, [fieldId, value]) => {
        if (
          !deepEqual(value, fieldValues[fieldId]) &&
          fields.some((f) => f.id === fieldId && !f.attributes.includes("ReadOnly"))
        ) {
          result[fieldId] = value;
        }
        return result;
      },
      {}
    );

    setIsSaving(true);
    const isSaved = await saveFieldValues(changedFieldValues);
    setIsSaving(false);

    if (isSaved) {
      setHasUnsavedEdits?.(false);
    }

    setPromptOnPageLeave(!isSaved);
  };

  const handleCancelClick = () => {
    if (fieldValuesState.fieldEditState === "editing") {
      handleFieldValuesStateChange({ isValid: true, fieldEditState: "canceling" });
    } else {
      handleFieldValuesStateChange({
        fieldValues: structuredClone(fieldValues),
        isValid: true,
        fieldEditState: "viewing",
      });
    }

    setPromptOnPageLeave(false);
    setHasUnsavedEdits?.(false);
  };

  const areThereEmptyRequiredFields = isAnyRequiredFieldEmpty(fields, fieldValuesState.fieldValues);

  const isStateValid = fieldValuesState.isValid && !areThereEmptyRequiredFields;

  const hasUnsavedFieldEdits = !deepEqual(fieldValuesState.fieldValues, fieldValues);

  const areButtonsDisabled =
    fieldValuesState.fieldEditState === "editing" || isSaving || !hasUnsavedChanges(fieldValuesState.fieldValues);

  return {
    fieldValuesState,
    lookupOptionsByObjectType,
    hasUnsavedFieldEdits,
    areButtonsDisabled,
    isSaveDisabled: areButtonsDisabled || !isStateValid,
    isBusy: isLoading || isFetchingLookupOptions || isSaving,
    handleFieldValuesStateChange,
    applyFieldValues,
    handleAddLookupOption,
    handleSaveClick,
    handleCancelClick,
  };
};

export default useFieldValuesEditor;
